import {
  ROOM_FILE_CHUNK_BYTES,
  ROOM_FILE_TAG_BYTES,
  type RoomFileEncryption,
} from '@andor/contracts/files';

/**
 * Client-side encryption of room files (format version 1, see
 * `RoomFileEncryptionSchema`). PBKDF2-SHA256 turns the password and a random
 * salt into an AES-256-GCM key. The file is cut into `ROOM_FILE_CHUNK_BYTES`
 * plaintext chunks; chunk `i` is sealed with the IV `noncePrefix ‖ u32be(i)`
 * and binds `i` plus a "final chunk" flag as additional data, so chunks cannot
 * be reordered, swapped between files or cut off without failing to decrypt.
 * Each sealed chunk (plaintext + 16-byte tag) fills exactly one upload part.
 * The password never leaves the browser; the server stores only ciphertext.
 */

export const FILE_KDF_ITERATIONS = 600_000;
const AAD_PREFIX = new TextEncoder().encode('andor-file-v1');
/** Reserved chunk index for the key check: never used by real chunks. */
const KEY_CHECK_INDEX = 0xffffffff;

export class WrongFilePasswordError extends Error {
  constructor() {
    super('Wrong password');
    this.name = 'WrongFilePasswordError';
  }
}

export interface FileCipher {
  key: CryptoKey;
  encryption: RoomFileEncryption;
}

/** A fresh key (random salt and nonce prefix) for one file. */
export async function createFileCipher(
  password: string,
  keySource: RoomFileEncryption['keySource'],
  iterations = FILE_KDF_ITERATIONS
): Promise<FileCipher> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const noncePrefix = crypto.getRandomValues(new Uint8Array(8));
  const key = await deriveKey(password, salt, iterations);
  const keyCheck = await sealChunk(key, noncePrefix, KEY_CHECK_INDEX, true, new Uint8Array(0));
  return {
    key,
    encryption: {
      v: 1,
      keySource,
      kdf: 'PBKDF2-SHA256',
      iterations,
      salt: toBase64(salt),
      noncePrefix: toBase64(noncePrefix),
      keyCheck: toBase64(keyCheck),
    },
  };
}

/** The file's key, or `WrongFilePasswordError` — checked without downloading. */
export async function unlockFile(
  password: string,
  encryption: RoomFileEncryption
): Promise<CryptoKey> {
  const key = await deriveKey(password, fromBase64(encryption.salt), encryption.iterations);
  try {
    await openChunk(
      key,
      fromBase64(encryption.noncePrefix),
      KEY_CHECK_INDEX,
      true,
      fromBase64(encryption.keyCheck)
    );
  } catch {
    throw new WrongFilePasswordError();
  }
  return key;
}

export function encryptedChunkCount(size: number): number {
  return Math.max(1, Math.ceil(size / ROOM_FILE_CHUNK_BYTES));
}

/** Sealed chunk `index` (0-based) of `file` — exactly one upload part. */
export async function encryptFileChunk(
  file: Blob,
  cipher: FileCipher,
  index: number
): Promise<Uint8Array<ArrayBuffer>> {
  const start = index * ROOM_FILE_CHUNK_BYTES;
  const plaintext = new Uint8Array(
    await file.slice(start, start + ROOM_FILE_CHUNK_BYTES).arrayBuffer()
  );
  return await sealChunk(
    cipher.key,
    fromBase64(cipher.encryption.noncePrefix),
    index,
    index === encryptedChunkCount(file.size) - 1,
    plaintext
  );
}

/**
 * Decrypts a stored file as it streams in, whatever the size of the incoming
 * pieces. A sealed chunk is only known to be the last one when nothing follows
 * it, so each chunk is opened once the next byte has arrived (or the stream
 * ended). Errors — altered, reordered, missing or cut-off chunks — fail the
 * stream, so a damaged file is never handed out, not even partially.
 */
export function createDecryptStream(
  key: CryptoKey,
  encryption: RoomFileEncryption
): TransformStream<Uint8Array, Uint8Array<ArrayBuffer>> {
  const sealedSize = ROOM_FILE_CHUNK_BYTES + ROOM_FILE_TAG_BYTES;
  const noncePrefix = fromBase64(encryption.noncePrefix);
  const pending: Uint8Array[] = [];
  let buffered = 0;
  let index = 0;
  const take = (size: number): Uint8Array<ArrayBuffer> => {
    const out = new Uint8Array(size);
    let offset = 0;
    while (offset < size) {
      const head = pending[0];
      const count = Math.min(head.length, size - offset);
      out.set(head.subarray(0, count), offset);
      offset += count;
      if (count === head.length) pending.shift();
      else pending[0] = head.subarray(count);
    }
    buffered -= size;
    return out;
  };
  return new TransformStream({
    async transform(bytes, controller) {
      pending.push(bytes);
      buffered += bytes.length;
      while (buffered > sealedSize) {
        controller.enqueue(await openChunk(key, noncePrefix, index++, false, take(sealedSize)));
      }
    },
    async flush(controller) {
      if (buffered === 0) throw new Error('The encrypted file is empty or cut off');
      controller.enqueue(await openChunk(key, noncePrefix, index++, true, take(buffered)));
    },
  });
}

/**
 * Decrypt a downloaded file held in a Blob. Each decrypted chunk becomes its
 * own small Blob as soon as it is ready, so the page holds about one chunk at
 * a time; the browser keeps (and may page out) the rest. Throws like
 * {@link createDecryptStream}.
 */
export async function decryptFile(
  stored: Blob,
  key: CryptoKey,
  encryption: RoomFileEncryption
): Promise<Blob> {
  const parts: Blob[] = [];
  const reader = stored.stream().pipeThrough(createDecryptStream(key, encryption)).getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    parts.push(new Blob([value]));
  }
  return new Blob(parts);
}

async function deriveKey(
  password: string,
  salt: Uint8Array<ArrayBuffer>,
  iterations: number
): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveKey']
  );
  return await crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt, iterations },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

function iv(noncePrefix: Uint8Array, index: number): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(12);
  bytes.set(noncePrefix.subarray(0, 8), 0);
  new DataView(bytes.buffer).setUint32(8, index);
  return bytes;
}

function additionalData(index: number, final: boolean): Uint8Array<ArrayBuffer> {
  const bytes = new Uint8Array(AAD_PREFIX.length + 5);
  bytes.set(AAD_PREFIX, 0);
  const view = new DataView(bytes.buffer);
  view.setUint32(AAD_PREFIX.length, index);
  view.setUint8(AAD_PREFIX.length + 4, final ? 1 : 0);
  return bytes;
}

async function sealChunk(
  key: CryptoKey,
  noncePrefix: Uint8Array,
  index: number,
  final: boolean,
  plaintext: Uint8Array<ArrayBuffer>
): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv(noncePrefix, index), additionalData: additionalData(index, final) },
      key,
      plaintext
    )
  );
}

async function openChunk(
  key: CryptoKey,
  noncePrefix: Uint8Array,
  index: number,
  final: boolean,
  sealed: Uint8Array<ArrayBuffer>
): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv(noncePrefix, index), additionalData: additionalData(index, final) },
      key,
      sealed
    )
  );
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
